import { ListItemText, Divider, IconButton, TextField, ListItem, Checkbox, FormControlLabel, ListItemAvatar, Avatar, List, useMediaQuery } from "@mui/material";
import { Check, MoreHoriz, Edit, DoDisturb, HealthAndSafety, BeachAccess, SyncAlt, Functions, NoteAdd, AccessTime, PunchClock, Celebration } from "@mui/icons-material";
import { useEffect, useState } from "react";
import formatDuration from "../../../../../../sharedComponents/FormatDuration";
import parseDuration from "../../../../../../sharedComponents/ParseDuration";
import SchoolIcon from '@mui/icons-material/School';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

function WorktimeList(props) {
    const [editActive, setEditActive] = useState(false);
    const [targetTimeNumber, setTargetTimeNumber] = useState("");
    const matches = useMediaQuery('(min-width:700px)');

    // Calculate targetTimeNumber when props change
    useEffect(() => {
        if (props.allMonths && props.selectedMonth) {
            const filteredMonths = filterAllMonths(props.allMonths, {
                year: props.selectedMonth.getFullYear(), 
                month: props.selectedMonth.getMonth() + 1
            });
            
            if (filteredMonths.length > 0) {
                const item = filteredMonths[0];
                const newTargetTimeNumber = (((parseDuration(item.target_working_hours).hours * 60) + parseDuration(item.target_working_hours).minutes) / 60).toLocaleString("de-DE", { maximumFractionDigits: 2 });
                setTargetTimeNumber(newTargetTimeNumber);
            }
        }
    }, [props.allMonths, props.selectedMonth]);

    const handleCheckbox = () => {
        props.setChangeFollowing(!props.changeFollowing);
    }

    const handleCancel = () => {
        setEditActive(false);
    }

    const handleEdit = () => {
        setEditActive(true);
    }

    const handleTargetTimeChange = (e) => {
        const regex = /^\d*,?\d{0,2}$/;
        if (e.target.value === "" || regex.test(e.target.value)) {
            setTargetTimeNumber(e.target.value);            
            props.targetTimeChange(e);
        }
    }

    function filterAllMonths(data, filters = {}) {
        if(!data) return [];

        return data.filter(object => {
            let matches = true;
            
            const [year, monthNum] = object.month.split('-').map(Number);
            
            if (filters.year !== undefined) {
                matches = matches && year === filters.year;
            }
            
            if (filters.month !== undefined) {
                matches = matches && monthNum === filters.month;
            }
            
            return matches;
        });
    }

    function monthListItems() {
        const filteredMonths = filterAllMonths(props.allMonths, {
            year: props.selectedMonth.getFullYear(), 
            month: props.selectedMonth.getMonth() + 1
        });

        if (filteredMonths.length === 0) {
            return <ListItemText primary={"Fehler: Monat liegt zu weit in der Zukunft oder hat zu diesem Zeitpunkt nicht existiert"} primaryTypographyProps={{ fontSize: '16px' }} sx={{ marginLeft: '25px' }} />;
        }

        let monthRender = [];
        
        filteredMonths.forEach((item, idx) => {
            const monthDateObj = new Date(item.month)
            const monthString = monthDateObj.toLocaleDateString('de', { month: 'long', year: 'numeric' });
            const monthISOString = monthDateObj.getFullYear() + '-' + monthDateObj.toLocaleDateString('de', { month: '2-digit'});            

            monthRender.push(
                <div key={idx}>
                    <ListItem>
                        <ListItemText primary={monthString} />
                    </ListItem>
                    <ListItem secondaryAction={
                        <>
                            {matches && editActive && (
                                <FormControlLabel
                                    control={<Checkbox checked={props.changeFollowing} onChange={() => handleCheckbox()} />}
                                    label="für folgende Monate übernehmen"
                                />
                            )}
                            {editActive && <>
                                <IconButton onClick={() => { props.save(monthISOString); setEditActive(false); }}>
                                    <Check />
                                </IconButton>
                                <IconButton onClick={handleCancel}>
                                    <DoDisturb />
                                </IconButton>
                            </>}
                            {!editActive && <IconButton onClick={() => handleEdit(idx)}><Edit /></IconButton>}
                            <IconButton onClick={() => props.addCorrection(item)}><MoreHoriz /></IconButton>
                        </>
                    }>
                        <div className="listitem-secondary">
                            <TextField
                                label={"Soll-Zeit (in Std)"}
                                size="small"
                                disabled={!editActive}
                                value={targetTimeNumber}
                                sx={{ width: '120px', marginLeft: '30px' }}
                                onChange={handleTargetTimeChange}
                            />
                        </div>
                    </ListItem>
                    <List>
                        {monthDetailsItems(item)}
                    </List>
                </div>
            );
        });

        return monthRender;
    }

    function monthDetailsItems(object) {
        let detailsList = [];
        const paidIllness = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.illness));
        const paidVacation = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.vacation));
        const paidHoliday = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.holiday));
        const paidSchool = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.school));
        const paidFurtherEducation = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.further_education));
        const paidVocationalSchool = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.vocational_school));
        const paidUniversity = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.university));
        const paidOther = formatDuration(parseDuration(object.total_payed_absence_duration_by_type?.other));
        const sumWorktime = formatDuration(parseDuration(object.total_tracked_shifts_duration));
        let result;
        let transferlastMonth;
        
        if (object.transfer_from_last_month === null) {
            transferlastMonth = "Noch nicht verfügbar"
        } else {
            transferlastMonth = formatDuration(parseDuration(object.transfer_from_last_month));
        }
        
        if (object.result === null) result = "Noch nicht verfügbar"
        else result = formatDuration(parseDuration(object.result));

        detailsList.push(
            <ListItem key="transferlastMonth" className="month-details-listitem" secondaryAction={<ListItemText primary={transferlastMonth} />}>
                <ListItemAvatar>
                    <Avatar>
                        <SyncAlt fontSize="small" />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText className="total-worktime-listitemtext" primary={"Transferstunden aus letztem Monat: "} />
            </ListItem>
        );
        
        detailsList.push(
            <ListItem key="worktarget" className="month-details-listitem" secondaryAction={<ListItemText primary={formatDuration(parseDuration(object.target_working_hours))} />}>
                <ListItemAvatar>
                    <Avatar>
                        <AccessTime fontSize="small" />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText primary={"Soll-Zeit: "} />
            </ListItem>
        );
        
        detailsList.push(
            <ListItem key="sumWorktime" className="month-details-listitem" secondaryAction={<ListItemText primary={sumWorktime} />}>
                <ListItemAvatar>
                    <Avatar>
                        <PunchClock fontSize="small" />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText className="total-worktime-listitemtext" primary={"Gesamt Arbeitsstunden: "} />
            </ListItem>
        );
        
        if (paidIllness !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidIllness" className="month-details-listitem" secondaryAction={<ListItemText primary={paidIllness} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <HealthAndSafety fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Krankenstunden: "} />
                </ListItem>
            );
        }
        
        if (paidVacation !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidVacation" className="month-details-listitem" secondaryAction={<ListItemText primary={paidVacation} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <BeachAccess fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Urlaubsstunden: "} />
                </ListItem>
            );
        }
        
        if (paidHoliday !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidHoliday" className="month-details-listitem" secondaryAction={<ListItemText primary={paidHoliday} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <Celebration fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Feiertagsstunden: "} />
                </ListItem>
            );
        }

        if (paidSchool !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidSchool" className="month-details-listitem" secondaryAction={<ListItemText primary={paidSchool} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <SchoolIcon fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Schulstunden: "} />
                </ListItem>
            );
        }

        if (paidFurtherEducation !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidFurtherEducation" className="month-details-listitem" secondaryAction={<ListItemText primary={paidFurtherEducation} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <LightbulbIcon fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Weiterbildung: "} />
                </ListItem>
            );
        }

        if (paidVocationalSchool !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidVocationalSchool" className="month-details-listitem" secondaryAction={<ListItemText primary={paidVocationalSchool} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <SchoolIcon fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Berufsschule: "} />
                </ListItem>
            );
        }

        if (paidUniversity !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidUniversity" className="month-details-listitem" secondaryAction={<ListItemText primary={paidUniversity} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <SchoolIcon fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Universität: "} />
                </ListItem>
            );
        }

        if (paidOther !== "00:00 Std") {
            detailsList.push(
                <ListItem key="paidOther" className="month-details-listitem" secondaryAction={<ListItemText primary={paidOther} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <MoreHorizIcon fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bezahlte Sonstigestunden: "} />
                </ListItem>
            );
        }
        
        object.corrections.forEach((item, index) => {
            detailsList.push(
                <ListItem key={`correction-${index}`} className="month-details-listitem" secondaryAction={
                    <div className="correction-secondary">
                        <ListItemText primary={Math.abs(item.working_time_correction.hours).toString().padStart(2, '0') + ":" + Math.abs(item.working_time_correction.minutes).toString().padStart(2, '0') + " Std"} />
                    </div>
                }>
                    <img className="hover-cursor-click" src={process.env.PUBLIC_URL + '/icons/x-icon.png'} onClick={() => props.deleteTimeCorrection(item.id)} width="25px" alt="Rotes X-Icon" />
                    <ListItemAvatar sx={{ marginLeft: '10px' }}>
                        <Avatar>
                            <NoteAdd fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Korrekturstunden: "} secondary={item.description} />
                </ListItem>
            );
        });
        
        detailsList.push(
            <div key="results">
                <Divider sx={{ borderBottomWidth: 5 }} variant="inset" />
                <ListItem className="month-details-listitem" secondaryAction={<ListItemText primary={result} primaryTypographyProps={{ style: { fontWeight: 'bold', fontSize: '20px' } }} />}>
                    <ListItemAvatar>
                        <Avatar>
                            <Functions fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={"Bilanz: "} primaryTypographyProps={{ style: { fontWeight: 'bold', fontSize: '20px' } }} />
                </ListItem>
            </div>
        );
        
        return detailsList;
    }

    return <>{monthListItems()}</>;
}

export default WorktimeList;